
import { useState, useEffect } from 'react';
import { trendingTracks, recommendedArtists, featuredPlaylists } from '../../mocks/musicData';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTrackSelect: (track: any) => void;
}

export default function SearchModal({ isOpen, onClose, onTrackSelect }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({
    tracks: [],
    artists: [],
    playlists: []
  });

  useEffect(() => {
    if (query.length > 0) {
      const filteredTracks = trendingTracks.filter(track =>
        track.title.toLowerCase().includes(query.toLowerCase()) ||
        track.artist.toLowerCase().includes(query.toLowerCase())
      );
      
      const filteredArtists = recommendedArtists.filter(artist =>
        artist.name.toLowerCase().includes(query.toLowerCase()) ||
        artist.genre.toLowerCase().includes(query.toLowerCase())
      );
      
      const filteredPlaylists = featuredPlaylists.filter(playlist =>
        playlist.title.toLowerCase().includes(query.toLowerCase()) ||
        playlist.description.toLowerCase().includes(query.toLowerCase())
      );

      setResults({
        tracks: filteredTracks,
        artists: filteredArtists,
        playlists: filteredPlaylists
      });
    } else {
      setResults({ tracks: [], artists: [], playlists: [] });
    }
  }, [query]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-lg"></i>
              </div>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for songs, artists, albums..."
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border-0 rounded-full text-base placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                autoFocus
              />
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[60vh]">
          {query.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <i className="ri-search-line text-4xl mb-4 block"></i>
              <p>Start typing to search for music</p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Tracks */}
              {results.tracks.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Songs</h3>
                  <div className="space-y-2">
                    {results.tracks.map((track: any) => (
                      <div
                        key={track.id}
                        onClick={() => {
                          onTrackSelect(track);
                          onClose();
                        }}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <img
                          src={track.imageUrl}
                          alt={track.title}
                          className="w-12 h-12 rounded-lg object-cover object-top"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{track.title}</h4>
                          <p className="text-sm text-gray-600 truncate">{track.artist}</p>
                        </div>
                        <button className="text-purple-600 hover:text-purple-700 transition-colors cursor-pointer">
                          <i className="ri-play-circle-line text-xl"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Artists */}
              {results.artists.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Artists</h3>
                  <div className="space-y-2">
                    {results.artists.map((artist: any) => (
                      <div
                        key={artist.id}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <img
                          src={artist.imageUrl}
                          alt={artist.name}
                          className="w-12 h-12 rounded-full object-cover object-top"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{artist.name}</h4>
                          <p className="text-sm text-gray-600 truncate">{artist.genre} • {artist.followers} followers</p>
                        </div>
                        <button className="text-purple-600 hover:text-purple-700 transition-colors cursor-pointer">
                          <i className="ri-user-add-line text-xl"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Playlists */}
              {results.playlists.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Playlists</h3>
                  <div className="space-y-2">
                    {results.playlists.map((playlist: any) => (
                      <div
                        key={playlist.id}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <img
                          src={playlist.imageUrl}
                          alt={playlist.title}
                          className="w-12 h-12 rounded-lg object-cover object-top"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{playlist.title}</h4>
                          <p className="text-sm text-gray-600 truncate">{playlist.trackCount} tracks • {playlist.plays} plays</p>
                        </div>
                        <button className="text-purple-600 hover:text-purple-700 transition-colors cursor-pointer">
                          <i className="ri-play-circle-line text-xl"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {results.tracks.length === 0 && results.artists.length === 0 && results.playlists.length === 0 && query.length > 0 && (
                <div className="text-center py-8 text-gray-500">
                  <i className="ri-search-line text-4xl mb-4 block"></i>
                  <p>No results found for "{query}"</p>
                  <p className="text-sm mt-2">Try searching for something else</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
